import type { HeartRiskFieldSources, HeartRiskInput, HeartRiskRace, HeartRiskSex } from "@/lib/heartRisk/types";

type PatientRecord = {
  id: string;
  full_name: string;
  data_nascimento: string | null;
  sexo: string | null;
};

type ProfileRecord = {
  age: number | null;
  sex: string | null;
  race: string | null;
  systolic_bp: number | null;
  diastolic_bp: number | null;
  total_cholesterol: number | null;
  hdl_cholesterol: number | null;
  diabetes: boolean | null;
  smoker: boolean | null;
  on_hypertension_treatment: boolean | null;
};

type LabRecord = {
  test_name: string;
  value: number | null;
  value_text: string | null;
  collected_at: string | null;
  created_at: string;
};

export interface HeartRiskPrefillResult {
  input: Partial<HeartRiskInput>;
  fieldSources: HeartRiskFieldSources;
}

const DEFAULT_INPUT: Partial<HeartRiskInput> = {
  race: "other",
  diabetes: false,
  smoker: false,
  onHypertensionTreatment: false,
};

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function mapSex(value: string | null | undefined): HeartRiskSex | undefined {
  const normalized = normalize(value ?? "");
  if (["m", "masculino", "male"].includes(normalized)) return "male";
  if (["f", "feminino", "female"].includes(normalized)) return "female";
  return undefined;
}

function mapRace(value: string | null | undefined): HeartRiskRace | undefined {
  const normalized = normalize(value ?? "");
  if (["black", "negra", "preta", "afrodescendente"].includes(normalized)) return "black";
  if (["white", "branca"].includes(normalized)) return "white";
  if (normalized) return "other";
  return undefined;
}

function getAge(birthDate: string | null): number | undefined {
  if (!birthDate) return undefined;
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return undefined;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : undefined;
}

function extractNumber(value: number | null, valueText: string | null): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (!valueText) return undefined;
  const parsed = Number(valueText.replace(",", ".").match(/-?\d+(\.\d+)?/)?.[0] ?? "");
  return Number.isFinite(parsed) ? parsed : undefined;
}

function pickLabValue(
  records: LabRecord[],
  aliases: string[],
  options?: { excludeAliases?: string[] },
): number | undefined {
  const normalizedAliases = aliases.map(normalize);
  const normalizedExcludeAliases = (options?.excludeAliases ?? []).map(normalize);
  for (const row of records) {
    const test = normalize(row.test_name);
    if (normalizedExcludeAliases.some((alias) => test.includes(alias))) {
      continue;
    }
    if (normalizedAliases.some((alias) => test.includes(alias))) {
      const numeric = extractNumber(row.value, row.value_text);
      if (typeof numeric === "number") return numeric;
    }
  }
  return undefined;
}

export function buildHeartRiskPrefill(params: {
  patient: PatientRecord;
  profile: ProfileRecord | null;
  labs: LabRecord[];
}): HeartRiskPrefillResult {
  const { patient, profile, labs } = params;
  const input: Partial<HeartRiskInput> = { ...DEFAULT_INPUT };
  const fieldSources: HeartRiskFieldSources = {};

  if (profile) {
    if (profile.age != null) {
      input.age = profile.age;
      fieldSources.age = "from_profile";
    }
    const profileSex = mapSex(profile.sex);
    if (profileSex) {
      input.sex = profileSex;
      fieldSources.sex = "from_profile";
    }
    const profileRace = mapRace(profile.race);
    if (profileRace) {
      input.race = profileRace;
      fieldSources.race = "from_profile";
    }
    if (profile.systolic_bp != null) {
      input.systolicBp = profile.systolic_bp;
      fieldSources.systolicBp = "from_profile";
    }
    if (profile.diastolic_bp != null) {
      input.diastolicBp = profile.diastolic_bp;
      fieldSources.diastolicBp = "from_profile";
    }
    if (profile.total_cholesterol != null) {
      input.totalCholesterol = profile.total_cholesterol;
      fieldSources.totalCholesterol = "from_profile";
    }
    if (profile.hdl_cholesterol != null) {
      input.hdlCholesterol = profile.hdl_cholesterol;
      fieldSources.hdlCholesterol = "from_profile";
    }
    if (profile.diabetes != null) {
      input.diabetes = profile.diabetes;
      fieldSources.diabetes = "from_profile";
    }
    if (profile.smoker != null) {
      input.smoker = profile.smoker;
      fieldSources.smoker = "from_profile";
    }
    if (profile.on_hypertension_treatment != null) {
      input.onHypertensionTreatment = profile.on_hypertension_treatment;
      fieldSources.onHypertensionTreatment = "from_profile";
    }
  }

  if (input.age == null) {
    const age = getAge(patient.data_nascimento);
    if (age != null) {
      input.age = age;
      fieldSources.age = "from_patient";
    }
  }

  if (!input.sex) {
    const sex = mapSex(patient.sexo);
    if (sex) {
      input.sex = sex;
      fieldSources.sex = "from_patient";
    }
  }

  if (input.systolicBp == null) {
    const systolic = pickLabValue(labs, ["pressao arterial sistolica", "pa sistolica", "systolic", "sbp"]);
    if (systolic != null) {
      input.systolicBp = systolic;
      fieldSources.systolicBp = "from_lab_result";
    }
  }

  if (input.diastolicBp == null) {
    const diastolic = pickLabValue(labs, ["pressao arterial diastolica", "pa diastolica", "diastolic", "dbp"]);
    if (diastolic != null) {
      input.diastolicBp = diastolic;
      fieldSources.diastolicBp = "from_lab_result";
    }
  }

  if (input.totalCholesterol == null) {
    const totalCholesterol = pickLabValue(labs, ["colesterol total", "total cholesterol", "cholesterol total"]);
    if (totalCholesterol != null) {
      input.totalCholesterol = totalCholesterol;
      fieldSources.totalCholesterol = "from_lab_result";
    }
  }

  if (input.hdlCholesterol == null) {
    const hdl = pickLabValue(labs, ["colesterol hdl", "hdl cholesterol", "hdl"], {
      excludeAliases: ["colesterol nao hdl", "nao hdl", "non hdl"],
    });
    if (hdl != null) {
      input.hdlCholesterol = hdl;
      fieldSources.hdlCholesterol = "from_lab_result";
    }
  }

  return { input, fieldSources };
}
